import { Button } from "@clockwise/design-system";
import { EventMovement, MovableUntil } from "@clockwise/schema";
import classNames from "classnames";
import { Duration } from "luxon";
import pluralize from "pluralize";
import { PropsWithChildren, default as React, ReactNode } from "react";
import {
  DefragRunFlowState,
  EventAutopilotStatusCardStatus,
  getDefragRunTag,
  getLocalDefragRunTime,
} from "../../util/event-history.util";
import { getRenderTimeZone, getTimeZoneDisplayName } from "../../util/time-zone.util";
import { openBillingPage } from "../../util/upgradeLinks.util";
import { NextDefragRunTag } from "../next-defrag-run-tag";

export interface EventAutopilotStatusCardProps {
  status: EventAutopilotStatusCardStatus;
  movableAt: EventMovement;
  movableUntil: MovableUntil;
  onUnpauseEvent: () => void;
  onViewPlans: () => void;
  showJoinExistingPlan?: boolean;
}

const Card = ({
  title,
  warning,
  action,
  children,
}: PropsWithChildren<{ title: ReactNode; warning?: boolean; action?: ReactNode }>) => (
  <div
    className={classNames("cw-border cw-border-solid cw-rounded  cw-py-2 cw-px-3 cw-mb-3", {
      "cw-border-default cw-bg-default": !warning,
      "cw-border-warning cw-bg-warning": warning,
    })}
    cw-id="event-status-card"
  >
    <div className={classNames("cw-body-sm cw-font-bold", { "cw-text-warning": warning })}>
      {title}
    </div>
    <div className="cw-caption">{children}</div>
    {action && <div className="cw-pt-2">{action}</div>}
  </div>
);

const EventOutsideTimeRange = ({ movableUntil }: { movableUntil: MovableUntil }) => {
  const eventMovementInHours = Number(Duration.fromISO(movableUntil.durationBefore).toFormat("h"));
  const eventMovementSuffix = pluralize("hours", eventMovementInHours);

  return (
    <Card title="This event's timing is set">
      Clockwise will not move this event because it begins in the next {eventMovementInHours}{" "}
      {eventMovementSuffix} or is in the past.
    </Card>
  );
};

const EventExceedsDefragRange = ({ movableAt }: { movableAt: EventMovement }) => {
  const eventMovement =
    movableAt.__typename === "AnyTime"
      ? "as your schedule changes"
      : `every weekday at ${getLocalDefragRunTime(movableAt.nextDefrag)} ${getTimeZoneDisplayName(
          getRenderTimeZone(),
        )}`;

  return (
    <Card title="Clockwise may update this event's timing">
      Clockwise will search for a better time for this event {eventMovement}.
    </Card>
  );
};

const TrialExpired = ({
  onViewPlans,
  showJoinExistingPlan,
  onGoToBilling,
}: {
  onViewPlans: () => void;
  showJoinExistingPlan?: boolean;
  onGoToBilling: () => void;
}) => {
  const description = "Your Teams trial has expired. Please upgrade to keep this flexible event.";

  const onClickCTA = () => {
    if (showJoinExistingPlan) {
      onGoToBilling();
    } else {
      onViewPlans();
    }
  };

  return (
    <Card
      title="Teams trial expired"
      warning
      action={
        <Button size="small" variant="outlined" onClick={onClickCTA}>
          {showJoinExistingPlan ? "Join a plan" : "Upgrade"}
        </Button>
      }
    >
      {description}
    </Card>
  );
};

const CanMove = ({ movableAt }: { movableAt: EventMovement }) => {
  const title = "Clockwise will optimize this event's timing";
  if (movableAt.__typename === "AnyTime") {
    return (
      <Card title={title}>
        Clockwise will search for a better time for this event throughout the day.
      </Card>
    );
  }

  const { nextDefrag } = movableAt;
  const { flowState } = getDefragRunTag(nextDefrag);
  const searchTense = flowState === DefragRunFlowState.Running ? "searches" : "will search";
  const defragRunTimeWithZone = `${getLocalDefragRunTime(nextDefrag)} ${getTimeZoneDisplayName(
    getRenderTimeZone(),
  )}`;

  return (
    <Card
      title={
        <>
          {title} <NextDefragRunTag nextDefrag={nextDefrag} />
        </>
      }
    >
      Clockwise {searchTense} for a better time for this event every weekday at&nbsp;
      {defragRunTimeWithZone}.
    </Card>
  );
};

const Paused = ({ onUnpauseEvent }: { onUnpauseEvent: () => void }) => {
  return (
    <Card
      title={"This event won't move"}
      warning
      action={
        <Button variant="outlined" size="small" onClick={onUnpauseEvent}>
          Let it move again
        </Button>
      }
    >
      This event was manually rescheduled to a specific time. Clockwise will not move it.
    </Card>
  );
};

export const EventAutopilotStatusCard = ({
  status,
  movableAt,
  movableUntil,
  onUnpauseEvent,
  onViewPlans,
  showJoinExistingPlan,
}: EventAutopilotStatusCardProps) => {
  const onGoToBilling = () => {
    openBillingPage();
  };

  switch (status) {
    case "EventOutsideTimeRange":
      return <EventOutsideTimeRange movableUntil={movableUntil} />;
    case "EventExceedsDefragRange":
      return <EventExceedsDefragRange movableAt={movableAt} />;
    case "CanMove":
      return <CanMove movableAt={movableAt} />;
    case "Paused":
      return <Paused onUnpauseEvent={onUnpauseEvent} />;
    case "TrialExpired":
      return (
        <TrialExpired
          onViewPlans={onViewPlans}
          onGoToBilling={onGoToBilling}
          showJoinExistingPlan={showJoinExistingPlan}
        />
      );
    case "OverQuota": //enum still exists in BE
      return null;
    case "Pinned":
    case "NoDuration":
    case "External":
    case "PrivateEvent":
    case "SmartHold":
      return null;
  }
};
